/**
 * The section-patch engine.
 *
 * The API guarantees (docs/architecture.md §4.2) that every mutation
 * returns the *full current state* of every section the change touched — including
 * sections the caller never mentioned but the change cascaded into. Reads use the
 * same section keys. So the client's entire state logic is:
 *
 *     state = sheet.sections        // on load
 *     state[k] = env.changed[k]     // on mutation, for each k
 *
 * That is the whole of this file. Three things follow, and all three are the
 * point rather than a limitation:
 *
 *  1. Mutations write the query cache directly — no `invalidateQueries`, no
 *     refetch. A skill edit already carries the recomputed stress and
 *     consequences with it.
 *  2. The client derives nothing. Refresh budgets, magic-item budgets, pyramid
 *     validity, resolved portrait URLs — all server-computed. A missing number is
 *     an API bug, not an invitation to compute it here.
 *  3. The server decides what changed. This code never diffs.
 */

/** Severity of a server-authored notice. Mirrors `Notice.Level` on the wire. */
export type NoticeLevel = 'info' | 'warn' | 'error'

/**
 * A server-authored message attached to a mutation. Shared by every domain, so
 * there is exactly one notice renderer in the client.
 *
 * `code` is stable and machine-readable (e.g. `stress.box.kept`); `message` is the
 * default human text. Key any localization off `code`, never off `message`.
 */
export interface Notice {
  level: NoticeLevel
  code: string
  message: string
}

/**
 * Anything carrying a versioned bag of sections. Every domain sheet satisfies this
 * plus its own identity fields (`characterId`, `campaignId`, `isOwner`, …), which
 * `applyEnvelope` preserves untouched.
 */
export interface HasSections<TSections extends object> {
  version: number
  sections: Partial<TSections>
}

/** The uniform mutation response. `changed` is keyed exactly like `sections`. */
export interface Envelope<TSections extends object> {
  version: number
  changed: Partial<TSections>
  notices: Notice[]
}

/**
 * Applies a mutation envelope to a sheet, returning a new sheet.
 *
 * Sections present in `changed` replace their counterparts wholesale — they are
 * already the full current state, so a deep merge would be wrong as well as
 * slower. Sections absent from `changed` are untouched.
 */
export function applyEnvelope<TSections extends object, TSheet extends HasSections<TSections>>(
  sheet: TSheet,
  envelope: Envelope<TSections>,
): TSheet {
  // The cast is TypeScript's spread-of-generic limitation, not a soundness gap:
  // every property of TSheet is carried over, and only `version` and `sections`
  // — both declared on HasSections — are replaced.
  return {
    ...sheet,
    version: envelope.version,
    sections: { ...sheet.sections, ...envelope.changed },
  } as TSheet
}

/**
 * The section-bag half of {@link applyEnvelope}, for callers holding sections
 * without the surrounding sheet. Fully type-safe, no cast.
 */
export function applySections<TSections extends object>(
  current: Partial<TSections>,
  changed: Partial<TSections>,
): Partial<TSections> {
  return { ...current, ...changed }
}

/** The section keys an envelope actually carried — useful for logging and tests. */
export function changedKeys<TSections extends object>(
  envelope: Envelope<TSections>,
): Array<keyof TSections> {
  return Object.keys(envelope.changed) as Array<keyof TSections>
}

/** Narrows a notice list to one severity, e.g. to decide whether to show a banner. */
export function noticesOf(notices: readonly Notice[], level: NoticeLevel): Notice[] {
  return notices.filter((notice) => notice.level === level)
}
